import type { NombreIcono } from '../../componentes/iconos';
import type { EstadoChip } from '../../componentes/Chip';
import type { Borrador, CambioBorrador, IdiomaWeb, MotivoBloqueo, MotivoPlegado, Retirada, SeccionBorrador, Sitio, TextosIdioma, TipoSeccion, VersionCompleta, Widget } from '../../api/ops/web';

/**
 * Reglas de «Web pública» sin estilos (13.1 a 13.5). El panel no decide nada de negocio: solo lee lo que da la API y
 * prepara lo que se ve.
 */

/**
 * Apartados de la barra Web · Widgets · Medición · API (G628, G644, ADR-0175): la entrada `web` llega con cualquiera de
 * sus cuatro permisos y dentro solo se ven los apartados que la persona puede ver. El Encargado y el Socio, solo
 * «Medición». Las claves de API van plegadas al final de Widgets; quien tiene `api.claves.gestionar` sin
 * `web.widgets.gestionar` (un rol propio, G644) las ve en su propio apartado «API».
 */
export interface ApartadoWeb {
    valor: 'web' | 'widgets' | 'medicion' | 'api';
    etiqueta: string;
    icono: NombreIcono;
    permiso: string;
}

export const APARTADOS_WEB: readonly ApartadoWeb[] = [
    { valor: 'web', etiqueta: 'Tu web', icono: 'globe', permiso: 'web.sitio.editar' },
    { valor: 'widgets', etiqueta: 'Widgets', icono: 'code', permiso: 'web.widgets.gestionar' },
    { valor: 'medicion', etiqueta: 'Medición', icono: 'chart-line', permiso: 'web.medicion.ver' },
];

export const APARTADO_API: ApartadoWeb = { valor: 'api', etiqueta: 'API', icono: 'code', permiso: 'api.claves.gestionar' };

export function apartadosVisibles(puede: (codigo: string) => boolean): ApartadoWeb[] {
    const visibles = APARTADOS_WEB.filter((a) => puede(a.permiso));
    return puede(APARTADO_API.permiso) && !visibles.some((a) => a.valor === 'widgets') ? [...visibles, APARTADO_API] : visibles;
}

/** Una dirección sin `https://` ni la barra final (G125). */
export function sinProtocolo(url: string | null | undefined): string {
    return (url ?? '').replace(/^https?:\/\//, '').replace(/\/$/, '');
}

/** Idiomas de los textos (G615): solo los que Reservaria traduce, el principal primero. */
export const IDIOMAS_WEB: readonly IdiomaWeb[] = ['es-ES', 'en'];
export const NOMBRE_IDIOMA: Record<IdiomaWeb, string> = { 'es-ES': 'Español', en: 'English' };

export function idiomasDelSelector(principal: IdiomaWeb): IdiomaWeb[] {
    return [principal, ...IDIOMAS_WEB.filter((i) => i !== principal)];
}

/** Los textos de un idioma; si aún no los tiene, null (en la web cae al principal, G615). */
export function textosDe(borrador: Pick<Borrador, 'locales'>, idioma: IdiomaWeb): TextosIdioma | null {
    return borrador.locales.find((l) => l.locale === idioma) ?? null;
}

/** Los textos de un idioma para editarlos: si no los tiene, parte de los del principal (lo que se ve hasta traducirlos). */
export function textosParaEditar(borrador: Pick<Borrador, 'locales' | 'default_locale'>, idioma: IdiomaWeb): TextosIdioma {
    const propios = textosDe(borrador, idioma);
    if (propios !== null) {
        return propios;
    }
    const principal = textosDe(borrador, borrador.default_locale);
    return { locale: idioma, seo: { ...(principal?.seo ?? { title: '', description: '' }) }, texts: {} };
}

/** Título de una sección en un idioma (o null si no lo tiene: «Sin traducir»). */
export function tituloPropio(borrador: Pick<Borrador, 'locales'>, idioma: IdiomaWeb, tipo: string): string | null {
    const titulos = textosDe(borrador, idioma)?.texts?.section_titles;
    const t = titulos?.[tipo];
    return typeof t === 'string' && t.trim() !== '' ? t : null;
}

/** Título que se ve de una sección: el del idioma, el del principal o el nombre del catálogo. */
export function tituloSeccion(borrador: Pick<Borrador, 'locales' | 'default_locale'>, idioma: IdiomaWeb, tipo: string, tipos: readonly TipoSeccion[]): string {
    return tituloPropio(borrador, idioma, tipo) ?? tituloPropio(borrador, borrador.default_locale, tipo) ?? tipos.find((t) => t.code === tipo)?.name ?? tipo;
}

/** Cambia (o añade) los textos de un idioma. */
export function conTextos(borrador: Borrador, textos: TextosIdioma): Borrador {
    const hay = borrador.locales.some((l) => l.locale === textos.locale);
    return { ...borrador, locales: hay ? borrador.locales.map((l) => (l.locale === textos.locale ? textos : l)) : [...borrador.locales, textos] };
}

/** Lo que se envía a `updateSiteDraft`: el borrador sin lo que solo se lee. */
export function cambioDe(b: Borrador): CambioBorrador {
    return {
        default_locale: b.default_locale,
        brand: b.brand,
        ...(b.hero === undefined ? {} : { hero: b.hero }),
        ...(b.links === undefined ? {} : { links: b.links }),
        ...(b.structured_data === undefined ? {} : { structured_data: b.structured_data }),
        sections: b.sections.map((s) => ({ section_type: s.section_type, visible: s.visible, ...(s.options === undefined ? {} : { options: s.options }), ...(s.image_media_id == null ? {} : { image_media_id: s.image_media_id }) })),
        locales: b.locales,
    };
}

/** Mueve una sección una posición arriba (-1) o abajo (+1). */
export function moverSeccion(secciones: readonly SeccionBorrador[], i: number, paso: -1 | 1): SeccionBorrador[] {
    const j = i + paso;
    if (j < 0 || j >= secciones.length) {
        return [...secciones];
    }
    const copia = [...secciones];
    const a = copia[i];
    const b = copia[j];
    if (a !== undefined && b !== undefined) {
        copia[i] = b;
        copia[j] = a;
    }
    return copia;
}

/** Chips de una sección en la lista (13.1): oculta, no contratada, no se publicará y no indexable. */
export function chipsSeccion(s: SeccionBorrador, tipo: TipoSeccion | undefined): { texto: string; estado: EstadoChip }[] {
    const chips: { texto: string; estado: EstadoChip }[] = [];
    if (tipo !== undefined && !tipo.entitlement_active) {
        chips.push({ texto: 'No contratado', estado: 'problem' });
    }
    if (!s.publishable) {
        chips.push({ texto: 'No se publicará', estado: 'pending' });
    }
    if (!s.visible) {
        chips.push({ texto: 'Oculta', estado: 'draft' });
    }
    if (tipo !== undefined && !tipo.indexable) {
        chips.push({ texto: 'No sale en buscadores', estado: 'neutral' });
    }
    return chips;
}

/**
 * «Lo cambiado desde la última versión» (revisión §5.9): comparar el borrador con el contenido de la versión publicada.
 * Una línea por parte de la web que cambió.
 */
export function cambiosDesde(b: Borrador, v: VersionCompleta | null, nombreSeccion: (tipo: string) => string): { parte: string; icono: NombreIcono; detalle: string }[] {
    if (v === null) {
        return [];
    }
    const igual = (x: unknown, y: unknown) => JSON.stringify(x ?? null) === JSON.stringify(y ?? null);
    const c = v.content;
    const cambios: { parte: string; icono: NombreIcono; detalle: string }[] = [];
    if (!igual(b.brand, c.brand)) {
        cambios.push({ parte: 'Marca', icono: 'palette', detalle: 'Colores, logo o letra' });
    }
    if (!igual(b.hero, c.hero) || !igual(b.locales.map((l) => [l.locale, l.texts?.tagline, l.texts?.about]), c.locales.map((l) => [l.locale, l.texts?.tagline, l.texts?.about]))) {
        cambios.push({ parte: 'Portada', icono: 'image', detalle: 'Portada, lema o «Sobre nosotros»' });
    }
    if (!igual(b.locales.map((l) => [l.locale, l.seo]), c.locales.map((l) => [l.locale, l.seo])) || !igual(b.structured_data, c.structured_data)) {
        cambios.push({ parte: 'Google y redes', icono: 'search', detalle: 'Título, descripción o tipo de negocio' });
    }
    const publicadas = new Map(c.sections.map((s) => [s.section_type, s]));
    const visibles = b.sections.filter((s) => s.visible && s.publishable);
    for (const s of visibles) {
        const p = publicadas.get(s.section_type);
        const titulos = (l: { texts?: { section_titles?: Record<string, unknown> } }) => l.texts?.section_titles?.[s.section_type];
        const tituloCambiado = !igual(b.locales.map(titulos), c.locales.map(titulos));
        if (p === undefined) {
            cambios.push({ parte: nombreSeccion(s.section_type), icono: 'plus', detalle: 'Sección nueva' });
        } else if (!igual(s.options ?? {}, p.options) || !igual(s.image_media_id ?? null, p.image_media_id ?? null) || tituloCambiado) {
            cambios.push({ parte: nombreSeccion(s.section_type), icono: 'pencil', detalle: 'Sección cambiada' });
        }
    }
    for (const p of c.sections) {
        if (!visibles.some((s) => s.section_type === p.section_type)) {
            cambios.push({ parte: nombreSeccion(p.section_type), icono: 'minus', detalle: 'Sale de la web' });
        }
    }
    if (!igual(visibles.map((s) => s.section_type), c.sections.map((s) => s.section_type).filter((t) => visibles.some((s) => s.section_type === t))) && cambios.every((x) => x.detalle !== 'Sección nueva' && x.detalle !== 'Sale de la web')) {
        cambios.push({ parte: 'Orden de las secciones', icono: 'arrow-up-down', detalle: 'Otro orden' });
    }
    if (!igual(b.links, c.links)) {
        cambios.push({ parte: 'Enlaces', icono: 'link', detalle: 'WhatsApp, mapa, Instagram o tus enlaces' });
    }
    return cambios;
}

/** Motivo de una retirada por abuso (G580, G612): el texto de la API, con el código como respaldo. */
const MOTIVO_RETIRADA: Record<Retirada['reason_code'], string> = {
    phishing: 'suplantación para robar datos',
    illegal_content: 'contenido ilegal',
    impersonation: 'suplantación de identidad',
    spam: 'spam',
    malware: 'software malicioso',
    other: 'uso no permitido',
};

export function motivoRetirada(r: Retirada): string {
    return r.reason_text.trim() !== '' ? r.reason_text : MOTIVO_RETIRADA[r.reason_code];
}

/** Estado de la web en la cabecera de «Tu web» (13.1, 13.3). */
export function chipSitio(s: Sitio): { texto: string; estado: EstadoChip } {
    if (s.status === 'withdrawn') {
        return { texto: 'Retirada', estado: 'problem' };
    }
    if (s.status === 'closed') {
        return { texto: s.closed_reasons.includes('seasonal_pause') ? 'Cerrada por temporada' : 'Cerrada', estado: 'pending' };
    }
    if (s.status === 'unpublished' || s.current_version == null) {
        return { texto: 'Sin publicar', estado: 'draft' };
    }
    return s.has_unpublished_changes ? { texto: 'Cambios sin publicar', estado: 'pending' } : { texto: 'Publicada', estado: 'confirmed' };
}

/** Texto del botón principal de «Tu web» (13.3). */
export function textoPublicar(s: Sitio, cambios: number): string {
    if (s.current_version == null) {
        return 'Publicar tu web';
    }
    if (s.status === 'withdrawn') {
        return 'Volver a publicar';
    }
    if (!s.has_unpublished_changes) {
        return 'Publicada';
    }
    return cambios > 0 ? `Publicar · ${String(cambios)} ${cambios === 1 ? 'cambio' : 'cambios'}` : 'Publicar';
}

/** Por qué un widget está plegado (G579): una frase por motivo. */
export const MOTIVO_PLEGADO: Record<MotivoPlegado, string> = {
    entitlement_missing: 'el bloque «Widget para su web» no está en tu plan',
    module_inactive: 'su módulo está apagado o sin conectar',
    read_only: 'el negocio está en solo lectura',
    seasonal_pause: 'el negocio está cerrado por temporada',
    key_revoked: 'su clave está revocada',
    platform_takedown: 'Reservaria lo ha retirado por abuso',
};

export const MOTIVO_BLOQUEO: Record<MotivoBloqueo, string> = {
    domain_not_allowed: 'Dominio no permitido',
    widget_paused: 'Widget pausado',
    entitlement_missing: 'No contratado',
    module_inactive: 'Módulo apagado',
    read_only: 'Solo lectura',
    seasonal_pause: 'Cerrado por temporada',
    key_revoked: 'Clave revocada',
    platform_takedown: 'Retirado por abuso',
};

/** Chip del estado de un widget en la lista (13.4): «Activo» va sin chip. */
export function chipWidget(w: Pick<Widget, 'status' | 'active_takedown'>): { texto: string; estado: EstadoChip } | null {
    if (w.active_takedown != null) {
        return { texto: 'Retirado por abuso', estado: 'problem' };
    }
    switch (w.status) {
        case 'paused':
            return { texto: 'Pausado', estado: 'neutral' };
        case 'folded':
            return { texto: 'Plegado', estado: 'pending' };
        case 'draft':
            return { texto: 'Borrador', estado: 'draft' };
        default:
            return null;
    }
}

/** Filtros de la lista de widgets (13.4), sin cifras. */
export const FILTROS_WIDGETS: readonly { etiqueta: string; estados: readonly Widget['status'][] | null }[] = [
    { etiqueta: 'Todos', estados: null },
    { etiqueta: 'Activos', estados: ['active'] },
    { etiqueta: 'Sin mostrarse', estados: ['paused', 'folded'] },
    { etiqueta: 'Borradores', estados: ['draft'] },
];

/**
 * Dominios de un widget escritos sin `https://` (G125, D-083): uno por línea o separados por comas, sin `www.` y en
 * minúsculas. La API los vuelve a normalizar y valida.
 */
export function dominiosDe(texto: string): string[] {
    return [
        ...new Set(
            texto
                .split(/[\s,]+/)
                .map((d) => d.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/^www\./, ''))
                .filter((d) => d !== ''),
        ),
    ];
}

/** «+8,0 %», «−2,5 %» o «0 %» frente al periodo anterior; null si antes no hubo nada. */
export function variacion(actual: number, anterior: number): string | null {
    if (anterior === 0) {
        return actual === 0 ? '0 %' : null;
    }
    const v = ((actual - anterior) / anterior) * 100;
    const texto = Math.abs(v).toLocaleString('es-ES', { maximumFractionDigits: 1, minimumFractionDigits: v === 0 ? 0 : 1 });
    return v === 0 ? '0 %' : `${v > 0 ? '+' : '−'}${texto} %`;
}

/** Periodos de la medición (13.5): desde, hasta (incluido) y granularidad; y el periodo anterior de la misma duración. */
export type Periodo = '7' | '30' | '12m';
export const PERIODOS: readonly { valor: Periodo; texto: string }[] = [
    { valor: '7', texto: 'Últimos 7 días' },
    { valor: '30', texto: 'Últimos 30 días' },
    { valor: '12m', texto: 'Últimos 12 meses' },
];

const iso = (d: Date) => `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function rangoDe(periodo: Periodo, hoy: Date = new Date()): { actual: { from: string; to: string }; anterior: { from: string; to: string }; granularity: 'day' | 'month' } {
    const dia = (base: Date, mas: number) => new Date(base.getFullYear(), base.getMonth(), base.getDate() + mas);
    if (periodo === '12m') {
        const desde = new Date(hoy.getFullYear(), hoy.getMonth() - 11, 1);
        const antesDesde = new Date(hoy.getFullYear(), hoy.getMonth() - 23, 1);
        return { actual: { from: iso(desde), to: iso(hoy) }, anterior: { from: iso(antesDesde), to: iso(dia(desde, -1)) }, granularity: 'month' };
    }
    const n = Number(periodo);
    const desde = dia(hoy, -(n - 1));
    return { actual: { from: iso(desde), to: iso(hoy) }, anterior: { from: iso(dia(desde, -n)), to: iso(dia(desde, -1)) }, granularity: 'day' };
}

/** Si un `options_schema` del catálogo tiene opciones que pintar. */
export function hayOpciones(esquema: unknown): boolean {
    const p = (esquema as { properties?: Record<string, unknown> } | null)?.properties;
    return p !== undefined && Object.keys(p).length > 0;
}
